import { Injectable, computed, signal } from '@angular/core';
import { CartItemModel, ProductModel } from '@app/models';

@Injectable({
    providedIn: 'root'
})
export class CartService
{
    // Computed signals for reactive cart data
    items = computed(() => this.cartItems());
    itemCount = computed(() => this.cartItems().reduce((total, item) => total + item.quantity, 0));
    totalPrice = computed(() => this.cartItems().reduce((total, item) => total + item.subtotal, 0));

    private cartItems = signal<CartItemModel[]>([]);

    addToCart(product: ProductModel, quantity: number = 1): void
    {
        const currentItems = this.cartItems();
        const existingItemIndex = currentItems.findIndex((item) => item.product.id === product.id);

        if (existingItemIndex >= 0)
        {
            const updatedQuantity = currentItems[existingItemIndex].quantity + quantity;
            const updatedItems = [...currentItems];
            updatedItems[existingItemIndex] = {
                ...currentItems[existingItemIndex],
                quantity: updatedQuantity,
                subtotal: updatedQuantity * (product.price ? Number(product.price) : 0),
            };
            this.cartItems.set(updatedItems);
        }
        else
        {
            // Add new item
            const newItem: CartItemModel = {
                product,
                quantity,
                subtotal: quantity * (product.price ? Number(product.price) : 0),
            };
            this.cartItems.set([...currentItems, newItem]);
        }
    }

    removeFromCart(productId: number): void
    {
        const updatedItems = this.cartItems().filter(item => item.product.id !== productId);
        this.cartItems.set(updatedItems);
    }

    updateQuantity(productId: number, quantity: number): void
    {
        if (quantity <= 0)
        {
            this.removeFromCart(productId);

            return;
        }

        const currentItems = this.cartItems();
        const existingItemIndex = currentItems.findIndex((item) => item.product.id === productId);

        if (existingItemIndex >= 0)
        {
            const updatedItems = [...currentItems];
            const updatedPrice = updatedItems[existingItemIndex].product.price;
            const updatedSubtotal = quantity * (updatedPrice ? Number(updatedPrice) : 0);
            updatedItems[existingItemIndex] = {
                ...updatedItems[existingItemIndex],
                quantity,
                subtotal: updatedSubtotal,
            };
            this.cartItems.set(updatedItems);
        }
    }

    getItemSubtotal(productId: number): number
    {
        const item = this.cartItems().find(item => item.product.id === productId);

        return item ? item.subtotal : 0;
    }

    getItemQuantity(productId: number): number
    {
        const item = this.cartItems().find(item => item.product.id === productId);

        return item ? item.quantity : 0;
    }

    clearCart(): void
    {
        this.cartItems.set([]);
    }

    isInCart(productId: number): boolean
    {
        return this.cartItems().some((item) => item.product.id === productId);
    }
}
